<script lang="ts">
  import PageTemplate from "@kontextwerk/web-sdk/ui/layout/PageTemplate.svelte"
  import AssistWidgetMount from "@kontextwerk/web-sdk/ui/assist/AssistWidgetMount.svelte"
  import { OrgRootBrandingMount, setFooterOverride } from "@kontextwerk/control-web-frame"
  import { location } from "../../_shared/_logic/store"
  import {
    fetchOrganizationPresentation,
    fetchWidgetConfig,
    type OrganizationPresentation,
    type PublicWidgetConfig,
    ApiError,
  } from "../../api/public/presentation"
  import {
    listContactMethods,
    type PublicAppointmentParticipantInput,
    type PublicContactMethodResponse,
    type PublicContactMethodsResponse,
  } from "../../api/public/appointment-portal"
  import { fetchPublicWorkOrder, type PublicWorkOrder } from "../../api/public/work-orders"
  import {
    CHAT_WIDGET_CONTEXT_UPDATED_EVENT,
    ensureContext,
    resolveWidgetCdnBase,
    resolveTranscriptionStream,
    spaNavigate,
    updateWidgetContext,
    type AssistWidgetConfig,
  } from "@kontextwerk/web-sdk/core"
  import BusinessPage from "./pages/Business"
  import ContactMethodsPage from "./pages/ContactMethods"
  import KehrAssistPage from "./pages/KehrAssist"
  import ProfilePage from "./pages/Profile"
  import PrivacyPage from "./pages/Privacy"
  import { buildOrgPresentationNavigation, buildOrgPresentationPaths } from "./navigation"
  import { resolveOrgPresentationMode, supportsTerminbot } from "./_shared/_logic/presentation-mode"
  import { applyOrgPresentationSeo } from "./_logic/seo"
  import { shouldShowOrgPresentationChatBubble } from "./_logic/chatBubble"
  import { buildAppointmentPortalContactMethodHints } from "./_shared/_logic/contact-method-hints"
  import {
    buildPendingContactMethodsFromParticipant,
    mergePendingContactMethods,
  } from "./_logic/pendingContactMethods"

  const { orgIdentifier } = $props<{ orgIdentifier: string }>()
  const DEFAULT_PUBLIC_PRESENTATION_WIDGET_PROJECT_NAME = "kehrblatt-chat-external"
  const PUBLIC_CHAT_START_PROFILE_ID = "public_chat_entry"
  const KEHRBLATT_PRIVACY_URL = "https://kehrblatt.de/datenschutz"
  const KEHRBLATT_MESSAGING_URL = "https://kehrblatt.de/messaging"
  const KEHRBLATT_IMPRINT_URL = "https://kehrblatt.de/impressum"

  let organization = $state<OrganizationPresentation | null>(null)
  let loading = $state(true)
  let error = $state<string | null>(null)
  let linkedOrder = $state<PublicWorkOrder | null>(null)
  let orderLoading = $state(false)
  let orderError = $state<string | null>(null)
  let widgetConfig = $state<PublicWidgetConfig | null>(null)
  let loadedPresentationKey = $state<string | null>(null)
  let presentationLoadRunId = 0
  let loadedOrderKey = $state<string | null>(null)
  let orderLoadRunId = 0
  let loadedPortalCode = $state<string | null>(null)
  let contactMethodsPreview = $state<PublicContactMethodsResponse | null>(null)
  let pendingContactMethods = $state<PublicContactMethodResponse[]>([])
  let contactMethodsPreviewLoading = $state(false)
  let loadedContactMethodsPreviewKey = $state<string | null>(null)
  let contactMethodsPreviewLoadRunId = 0

  const presentationBasePath = $derived(
    `/presentation/${encodeURIComponent(organization?.slug ?? orgIdentifier)}`,
  )
  const brandingRouteScopeKey = $derived(`presentation:${organization?.slug ?? orgIdentifier}`)

  const normalizeText = (value?: string | null): string | null => {
    const text = value?.trim()

    return text || null
  }

  const searchParams = $derived(
    (() => {
      const rawSearch =
        $location.search ?? (typeof window !== "undefined" ? window.location.search : "")

      return new URLSearchParams(rawSearch ?? "")
    })(),
  )

  const pickParam = (params: URLSearchParams, ...keys: string[]): string | null => {
    for (const key of keys) {
      const value = params.get(key)

      if (value && value.trim()) {
        return value.trim()
      }
    }

    return null
  }

  const linkedOrderId = $derived.by(() =>
    pickParam(searchParams, "orderId", "order_id", "workOrderId", "work_order_id"),
  )
  const contactStepCodeParam = $derived.by(() => {
    const code = pickParam(searchParams, "code", "Code", "CODE")

    return code ? code.toUpperCase() : null
  })
  const propertyCodeParam = $derived.by(() => {
    const code = pickParam(searchParams, "property_code", "propertyCode", "PROPERTY_CODE")

    return code ? code.toUpperCase() : null
  })
  const effectiveContactStepCode = $derived(contactStepCodeParam ?? loadedPortalCode)
  const orgIdParam = $derived(pickParam(searchParams, "organizationId", "organization_id", "orgId"))
  const projectIdParam = $derived(pickParam(searchParams, "projectId", "project_id", "project"))
  const profileTabParam = $derived(pickParam(searchParams, "tab"))
  const defaultCdnBase = (): string =>
    resolveWidgetCdnBase(typeof window === "undefined" ? null : window.location.hostname, {
      kontextwerkBase: "https://widgets.kehrblatt-control.kontextwerk.org",
    })
  const syncChatContext = () => {
    updateWidgetContext({
      page: "PUBLIC_PRESENTATION",
      chatStartProfileId: PUBLIC_CHAT_START_PROFILE_ID,
      organizationId: organization?.id ?? orgIdParam ?? orgIdentifier ?? null,
      organizationName: organization?.name ?? null,
      projectId: widgetConfig?.project_id ?? projectIdParam ?? null,
      projectName: widgetConfig?.project_name ?? organization?.name ?? null,
      realizationId: widgetConfig?.realization_id ?? null,
    })
    const ctx = ensureContext()

    if (!ctx) return

    ctx.page = "PUBLIC_PRESENTATION"
    ctx.chat_start_profile_id = PUBLIC_CHAT_START_PROFILE_ID
    ctx.organization_id = organization?.id ?? orgIdParam ?? orgIdentifier ?? null
    ctx.organization_name = organization?.name ?? null
    ctx.project_id = widgetConfig?.project_id ?? projectIdParam ?? null
    ctx.project_name = widgetConfig?.project_name ?? organization?.name ?? null
    ctx.assist_mode = "public"
    ctx.public_assist = true
    ctx.public_mode = true
    ctx.mode = "public"

    if (effectiveContactStepCode) {
      ctx.contact_step_code = effectiveContactStepCode
      ctx.code = effectiveContactStepCode
      ctx.source = "public_contact_step_link"
    } else {
      delete ctx.contact_step_code
      delete ctx.code

      if (ctx.source === "public_contact_step_link") {
        delete ctx.source
      }
    }

    if (propertyCodeParam) {
      ctx.property_code = propertyCodeParam

      if (!effectiveContactStepCode) {
        ctx.source = "public_property_link"
      }
    } else {
      delete ctx.property_code

      if (ctx.source === "public_property_link") {
        delete ctx.source
      }
    }

    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent(CHAT_WIDGET_CONTEXT_UPDATED_EVENT, {
          detail: { chatStartProfileId: PUBLIC_CHAT_START_PROFILE_ID },
        }),
      )
    }
  }

  const resolvedApiBase = $derived(widgetConfig?.api_base?.trim() || null)
  const resolvedTranscriptionStream = $derived(
    resolvedApiBase
      ? widgetConfig?.transcription_stream?.trim() ||
          resolveTranscriptionStream(resolvedApiBase, null)
      : null,
  )
  const resolvedCdnBase = $derived(widgetConfig?.cdn_base?.trim() || defaultCdnBase())
  const embeddedChatConfig = $derived.by<AssistWidgetConfig | null>(() => {
    const config = widgetConfig
    const projectSelector = config?.project_name?.trim() || config?.project_slug?.trim() || null

    if (!config || !projectSelector || !resolvedApiBase || !resolvedCdnBase) {
      return null
    }

    return {
      projectSlug: projectSelector,
      apiBase: resolvedApiBase,
      transcriptionStream: resolvedTranscriptionStream,
      cdnBase: resolvedCdnBase,
      realizationId: config.realization_id ?? null,
      organizationId: organization?.id ?? null,
      projectId: config.project_id ?? null,
    }
  })

  const presentationMode = $derived(
    resolveOrgPresentationMode(organization, effectiveContactStepCode),
  )
  const presentationPaths = $derived(buildOrgPresentationPaths(presentationBasePath))
  const organizationDisplayName = $derived(
    normalizeText(organization?.public_name) ?? normalizeText(organization?.name) ?? "Betrieb",
  )
  const organizationPrivacyUrl = $derived(
    normalizeText(organization?.data_privacy_url) ?? presentationPaths.privacy,
  )
  const hasContactMethodScope = $derived(contactMethodsPreview?.available === true)
  const appointmentPortalContactMethodHints = $derived(
    buildAppointmentPortalContactMethodHints(contactMethodsPreview),
  )
  const visibleContactMethodsPreview = $derived(
    mergePendingContactMethods(contactMethodsPreview, pendingContactMethods),
  )

  const pageNavigation = $derived.by(() =>
    buildOrgPresentationNavigation({
      context: { presentationMode, hasContactMethodScope },
      paths: presentationPaths,
      snippets: {
        profile: aboutContent,
        assist: assistContent,
        business: businessContent,
        privacy: privacyContent,
        contactMethods: contactMethodsContent,
      },
    }),
  )

  const resolveWidgetConfigFor = async (nextOrganization: OrganizationPresentation | null) => {
    if (!nextOrganization) {
      return null
    }

    if (!supportsTerminbot(nextOrganization)) {
      return null
    }

    const projectName =
      nextOrganization.widget_project_name?.trim() ||
      DEFAULT_PUBLIC_PRESENTATION_WIDGET_PROJECT_NAME

    if (!projectName) {
      return null
    }

    try {
      return await fetchWidgetConfig({
        projectName,
        organizationId: nextOrganization.id,
        realizationId: nextOrganization.widget_realization_id ?? null,
        branding: nextOrganization.branding ?? null,
      })
    } catch (err) {
      console.error("widget_config_load_failed", err)

      return null
    }
  }

  const loadForOrganization = async (
    targetIdentifier: string,
    { force = false }: { force?: boolean } = {},
  ) => {
    const normalizedIdentifier = targetIdentifier?.trim()

    if (!normalizedIdentifier) {
      error = "Organisation konnte nicht ermittelt werden."
      organization = null
      widgetConfig = null

      return
    }

    if (!force && loadedPresentationKey === normalizedIdentifier) return

    const runId = ++presentationLoadRunId
    loading = true
    error = null
    try {
      const nextOrganization = await fetchOrganizationPresentation(normalizedIdentifier)
      const nextWidgetConfig = await resolveWidgetConfigFor(nextOrganization)

      if (runId !== presentationLoadRunId) return

      organization = nextOrganization
      widgetConfig = nextWidgetConfig
      loadedPresentationKey = normalizedIdentifier
    } catch (err) {
      if (runId !== presentationLoadRunId) return

      console.error("presentation_load_failed", err)
      let message = (err as { message?: string })?.message ?? "Profil konnte nicht geladen werden."

      if (err instanceof ApiError) {
        message = err.status === 404 ? "Organisation wurde nicht gefunden." : err.message
      }

      error = message || "Profil konnte nicht geladen werden."
      organization = null
      widgetConfig = null
      loadedPresentationKey = null
    } finally {
      if (runId === presentationLoadRunId) {
        loading = false
      }
    }
  }

  const load = async () => loadForOrganization(orgIdentifier, { force: true })

  $effect(() => {
    const targetIdentifier = orgIdentifier
    void Promise.resolve().then(() => loadForOrganization(targetIdentifier))
  })

  const loadOrderForId = async (orderId: string | null) => {
    if (!orderId) {
      linkedOrder = null
      orderError = null
      loadedOrderKey = null

      return
    }

    if (loadedOrderKey === orderId) return

    const runId = ++orderLoadRunId
    orderLoading = true
    orderError = null
    try {
      const nextLinkedOrder = await fetchPublicWorkOrder(orderId)

      if (runId !== orderLoadRunId) return

      linkedOrder = nextLinkedOrder
      loadedOrderKey = orderId
    } catch (err) {
      if (runId !== orderLoadRunId) return

      console.error("public_work_order_load_failed", err)

      if (err instanceof ApiError) {
        orderError = err.status === 404 ? "Auftrag wurde nicht gefunden." : err.message
      } else {
        orderError = "Auftrag konnte nicht geladen werden."
      }

      linkedOrder = null
      loadedOrderKey = null
    } finally {
      if (runId === orderLoadRunId) {
        orderLoading = false
      }
    }
  }

  $effect(() => {
    const orderId = linkedOrderId
    void Promise.resolve().then(() => loadOrderForId(orderId))
  })

  const resetContactMethodsPreview = () => {
    contactMethodsPreview = null
    pendingContactMethods = []
    loadedContactMethodsPreviewKey = null
    contactMethodsPreviewLoading = false
  }

  const loadContactMethodsPreview = async (
    targetOrgIdentifier: string,
    targetCode: string,
    key: string,
    options: { clearCurrent?: boolean } = {},
  ) => {
    const runId = ++contactMethodsPreviewLoadRunId
    contactMethodsPreviewLoading = true

    if (options.clearCurrent !== false) {
      contactMethodsPreview = null
    }

    try {
      const response = await listContactMethods(targetOrgIdentifier, targetCode)

      if (runId !== contactMethodsPreviewLoadRunId) return

      contactMethodsPreview = response
      pendingContactMethods =
        mergePendingContactMethods(response, pendingContactMethods)?.contact_methods.filter(
          (method) => method.is_pending,
        ) ?? pendingContactMethods
      loadedContactMethodsPreviewKey = key
    } catch (err) {
      if (runId !== contactMethodsPreviewLoadRunId) return

      console.error("contact_methods_preview_load_failed", err)
      contactMethodsPreview = null
      loadedContactMethodsPreviewKey = key
    } finally {
      if (runId === contactMethodsPreviewLoadRunId) {
        contactMethodsPreviewLoading = false
      }
    }
  }

  const refreshContactMethodsPreview = () => {
    if (!organization || loading || error || !effectiveContactStepCode) return

    const targetOrgIdentifier = organization.slug ?? orgIdentifier
    const key = `${targetOrgIdentifier}:${effectiveContactStepCode}`
    void loadContactMethodsPreview(targetOrgIdentifier, effectiveContactStepCode, key, {
      clearCurrent: false,
    })
  }

  const updateLoadedPortalCode = (nextCode: string | null) => {
    const normalized = nextCode?.trim().toUpperCase() || null
    loadedPortalCode = normalized
  }

  const addPendingContactMethods = (participant: PublicAppointmentParticipantInput) => {
    const nextPending = buildPendingContactMethodsFromParticipant(participant)

    if (!nextPending.length) return

    const currentById = new Map(pendingContactMethods.map((method) => [method.id, method]))
    for (const method of nextPending) {
      currentById.set(method.id, method)
    }
    pendingContactMethods = Array.from(currentById.values())

    refreshContactMethodsPreview()
  }

  $effect(() => {
    const urlCode = contactStepCodeParam

    if (urlCode) {
      loadedPortalCode = urlCode

      return
    }

    if (typeof window === "undefined") {
      loadedPortalCode = null

      return
    }

    const browserCode = pickParam(
      new URLSearchParams(window.location.search),
      "code",
      "Code",
      "CODE",
    )

    if (!browserCode) {
      loadedPortalCode = null
    }
  })

  $effect(() => {
    if (!organization || loading || error || !effectiveContactStepCode) {
      resetContactMethodsPreview()

      return
    }

    const targetOrgIdentifier = organization.slug ?? orgIdentifier
    const key = `${targetOrgIdentifier}:${effectiveContactStepCode}`

    if (loadedContactMethodsPreviewKey === key || contactMethodsPreviewLoading) return

    void loadContactMethodsPreview(targetOrgIdentifier, effectiveContactStepCode, key)
  })

  const currentPresentationPath = $derived(($location.path ?? "").replace(/\/+$/, "") || "/")
  const currentPresentationPage = $derived(
    currentPresentationPath === presentationPaths.assist
      ? "assist"
      : currentPresentationPath === presentationPaths.privacy
        ? "privacy"
        : currentPresentationPath === presentationPaths.contactMethods
          ? "contactMethods"
          : currentPresentationPath === presentationPaths.business ||
              currentPresentationPath === presentationPaths.legacyContact
            ? "business"
            : "profile",
  )
  const showOrgChatBubble = $derived(
    shouldShowOrgPresentationChatBubble({
      currentPage: currentPresentationPage,
      presentationMode,
      hasWidgetConfig: Boolean(embeddedChatConfig),
    }),
  )

  $effect(() => {
    if (!organization || loading || error) return

    if (currentPresentationPath !== presentationPaths.legacyContact) return

    const search = $location.search ?? ""
    spaNavigate(`${presentationPaths.business}${search}`, { replace: true, noSearchParam: false })
  })

  $effect(() => {
    if (!organization || loading || error) return

    const legacyTab = (profileTabParam ?? "").trim().toLowerCase()

    if (!supportsTerminbot(organization) || (legacyTab !== "1" && legacyTab !== "bot")) return

    if (currentPresentationPath !== presentationBasePath) return

    const nextSearch = new URLSearchParams($location.search ?? "")
    nextSearch.delete("tab")
    const query = nextSearch.toString()
    spaNavigate(`${presentationPaths.assist}${query ? `?${query}` : ""}`, {
      replace: true,
      noSearchParam: false,
    })
  })

  // effect-ok: store-bridge - presentation pages replace the shared legal footer while mounted.
  $effect(() => {
    if (!organization || loading || error) {
      setFooterOverride(null)

      return
    }

    setFooterOverride({
      branding: organization.branding ?? null,
      notice: `Terminabstimmung im Auftrag von ${organizationDisplayName}. Technische Plattform: KehrBlatt.`,
      legalLinks: [
        {
          label: `Datenschutz ${organizationDisplayName}`,
          href: organizationPrivacyUrl,
        },
        {
          label: "KehrBlatt-Datenschutz",
          href: KEHRBLATT_PRIVACY_URL,
        },
        {
          label: "KehrBlatt-Messaging",
          href: KEHRBLATT_MESSAGING_URL,
        },
        {
          label: "KehrBlatt-Impressum",
          href: KEHRBLATT_IMPRINT_URL,
        },
      ],
    })

    return () => {
      setFooterOverride(null)
    }
  })

  $effect(syncChatContext)

  $effect(() =>
    applyOrgPresentationSeo({
      organization,
      presentationMode,
      hasCode: Boolean(effectiveContactStepCode),
      page: currentPresentationPage,
      presentationBasePath,
    }),
  )
</script>

<OrgRootBrandingMount
  organizationId={organization?.id ?? null}
  routeScopeKey={brandingRouteScopeKey}
  branding={organization?.branding ?? null}
  source="kehrblatt-presentation"
  fetchWhenMissing={false}
/>

<PageTemplate
  viewKey={`presentation:${orgIdentifier}`}
  title={organization?.name ?? "Betriebsseite"}
  {pageNavigation}
  locationStore={location}
/>

{#if showOrgChatBubble && embeddedChatConfig}
  <AssistWidgetMount config={embeddedChatConfig} />
{/if}

{#snippet aboutContent()}
  <ProfilePage
    {organization}
    {loading}
    {error}
    {load}
    {presentationMode}
    orgIdentifier={organization?.slug ?? orgIdentifier}
    contactStepCode={effectiveContactStepCode}
    contactMethodHints={appointmentPortalContactMethodHints}
    onPortalCodeResolved={updateLoadedPortalCode}
    onContactMethodSubmitted={addPendingContactMethods}
    {linkedOrder}
    {orderLoading}
    {orderError}
  />
{/snippet}

{#snippet assistContent()}
  <KehrAssistPage {loading} {error} {load} {embeddedChatConfig} />
{/snippet}

{#snippet businessContent()}
  <BusinessPage
    {organization}
    {loading}
    {error}
    {load}
    {presentationMode}
    hasCode={Boolean(effectiveContactStepCode)}
  />
{/snippet}

{#snippet privacyContent()}
  <PrivacyPage {organization} {loading} {error} {load} />
{/snippet}

{#snippet contactMethodsContent()}
  <ContactMethodsPage
    {organization}
    {loading}
    {error}
    {load}
    orgIdentifier={organization?.slug ?? orgIdentifier}
    contactStepCode={effectiveContactStepCode}
    initialContactMethodsState={visibleContactMethodsPreview}
  />
{/snippet}

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";
</style>
